import styled from '@emotion/styled';
import React, { useCallback, useState } from 'react';
import { ModalSidebarContent } from '../../../../components/ModalSidebar';
import { EmployeeOption } from '../../../../components/Selects/EmployeeOptionLabel';
import { useUserInfo } from '../../../../context/UserInfo';
import { useIsManagerMode } from '../../../../hooks/useIsManagerMode';
import { ManagerRole } from '../../../../__generated__/graphql';
import { GoalRelatedGoal } from '../types';
import { GoalCreateOptions } from '../useGoalSelectOptions';
import { GoalAlignedToConnected } from './GoalAlignedTo/GoalAlignedToConnected';
import { GoalCommonFields } from './GoalCommonFields';
import { GoalCreateTypeField } from './GoalCreateTypeField';
import { GoalSaveButton } from './GoalSaveButton';
import { useCreateObjectiveForm } from './useObjectiveForm';
import { useCreateObjective } from './useObjectiveMutations';
import { useGoalsContext } from '../GoalsContext/GoalsContext';
import {
  GoalFormApi,
  GoalFormProvider,
  scrollToFirstError,
} from './goalFormState';
import { ObjectiveFormData } from './types';

type Props = {
  owner?: EmployeeOption;
  employeeId?: string;
  options: GoalCreateOptions;
  onClose: () => void;
};

export const GoalCreateForm: React.FC<Props> = ({
  onClose,
  employeeId,
  ...restProps
}) => {
  const userInfo = useUserInfo();

  const { id: meId } = userInfo;
  const { hasManagerRole } = useIsManagerMode();
  const { isManagerView, objectiveMode } = useGoalsContext();

  const defaultSelectedEmployee = employeeId || (!isManagerView ? meId : '');
  const form = useCreateObjectiveForm(defaultSelectedEmployee, objectiveMode);
  const [createObjective] = useCreateObjective(defaultSelectedEmployee);

  const handleSubmit = useCallback(async () => {
    const errors = form.validate();
    if (errors) {
      scrollToFirstError(errors);
      return;
    }
    form.setIsSubmitting(true);
    try {
      await createObjective(form.values);
    } catch {
      form.setIsSubmitting(false);
      return;
    }
    onClose();
  }, [form, createObjective, onClose]);

  return (
    <GoalFormProvider form={form}>
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}>
        <ModalSidebarContent
          body={
            <div>
              {hasManagerRole(ManagerRole.DirectManager) ? (
                <GoalCreateTypeField isDisabled={form.isSubmitting} />
              ) : (
                <input type="hidden" value={form.values.type} readOnly />
              )}
              <GoalCommonFields
                {...restProps}
                mode="create"
                goalAlignedTo={<AlignedToSection form={form} />}
              />
            </div>
          }
          footer={<GoalSaveButton disabled={form.isSubmitting} />}
        />
      </Form>
    </GoalFormProvider>
  );
};

type AlignedToSectionProps = {
  form: GoalFormApi<ObjectiveFormData>;
};

const AlignedToSection: React.FC<AlignedToSectionProps> = ({ form }) => {
  const { developmentFrameworkEnabled } = useGoalsContext();
  const [alignedTo, setAlignedTo] = useState<GoalRelatedGoal[]>([]);

  const applyChange = useCallback(
    (next: GoalRelatedGoal[]) => {
      setAlignedTo(next);
      form.setFieldValue(
        'alignedTo',
        next.map((g) => ({ id: g.id, type: g.type })),
      );
    },
    [form],
  );

  const handleAdd = useCallback(
    async (goal: GoalRelatedGoal) => {
      applyChange([...alignedTo, goal]);
    },
    [alignedTo, applyChange],
  );

  const handleRemove = useCallback(
    async (goal: GoalRelatedGoal) => {
      applyChange(alignedTo.filter((g) => g.id !== goal.id));
    },
    [alignedTo, applyChange],
  );

  if (form.values.type !== 'employee' || !developmentFrameworkEnabled) {
    return null;
  }

  return (
    <div className="mb-3">
      <GoalAlignedToConnected
        goalId={{ type: 'employee', id: '' }}
        alignedTo={alignedTo}
        onAdd={handleAdd}
        onRemove={handleRemove}
        isProcessing={false}
      />
    </div>
  );
};

const Form = styled('form')({
  display: 'contents',
});
